import React, { useState, useMemo } from 'react';
import { FilterOutlined, DownOutlined } from '@ant-design/icons';
import InteractiveChart from './InteractiveChart';
import InteractiveTable from './InteractiveTable';

// Dropdown filter component
const FilterDropdown = ({ label, options = [], value, onChange, multiple = false }) => {
  const [isOpen, setIsOpen] = useState(false);

  const selectedLabel = multiple
    ? value?.length > 0
      ? `${value.length} selected`
      : label
    : options.find((o) => o.value === value)?.label || label;

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
      >
        <FilterOutlined className="text-gray-400" />
        <span className="text-gray-700">{selectedLabel}</span>
        <DownOutlined
          style={{
            fontSize: 10,
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
          }}
          className="text-gray-400"
        />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute z-20 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg py-1 max-h-60 overflow-y-auto">
            {multiple && (
              <button
                onClick={() => {
                  onChange([]);
                }}
                className="w-full px-3 py-2 text-left text-sm text-gray-500 hover:bg-gray-50"
              >
                Clear all
              </button>
            )}
            {options.map((option) => {
              const isSelected = multiple
                ? value?.includes(option.value)
                : value === option.value;
              return (
                <button
                  key={option.value}
                  onClick={() => {
                    if (multiple) {
                      const newValue = isSelected
                        ? value.filter((v) => v !== option.value)
                        : [...(value || []), option.value];
                      onChange(newValue);
                    } else {
                      onChange(option.value);
                      setIsOpen(false);
                    }
                  }}
                  className={`w-full px-3 py-2 text-left text-sm flex items-center justify-between hover:bg-gray-50 ${
                    isSelected ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700'
                  }`}
                >
                  {option.label}
                  {isSelected && (
                    <span className="text-indigo-600">✓</span>
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

// Range slider component
const RangeSlider = ({ label, min = 0, max = 100, value, onChange, step = 1 }) => {
  const [localValue, setLocalValue] = useState(value || [min, max]);

  const handleChange = (index, newVal) => {
    const newValue = [...localValue];
    newValue[index] = Number(newVal);
    if (index === 0 && newValue[0] > newValue[1]) {
      newValue[0] = newValue[1];
    }
    if (index === 1 && newValue[1] < newValue[0]) {
      newValue[1] = newValue[0];
    }
    setLocalValue(newValue);
    onChange(newValue);
  };

  return (
    <div className="flex flex-col gap-2">
      <label className="text-sm font-medium text-gray-700">{label}</label>
      <div className="flex items-center gap-3">
        <input
          type="number"
          value={localValue[0]}
          onChange={(e) => handleChange(0, e.target.value)}
          min={min}
          max={max}
          step={step}
          className="w-20 px-2 py-1 text-sm border border-gray-200 rounded"
        />
        <div className="flex-1 relative h-2 bg-gray-200 rounded">
          <div
            className="absolute h-full bg-indigo-500 rounded"
            style={{
              left: `${((localValue[0] - min) / (max - min)) * 100}%`,
              right: `${100 - ((localValue[1] - min) / (max - min)) * 100}%`,
            }}
          />
        </div>
        <input
          type="number"
          value={localValue[1]}
          onChange={(e) => handleChange(1, e.target.value)}
          min={min}
          max={max}
          step={step}
          className="w-20 px-2 py-1 text-sm border border-gray-200 rounded"
        />
      </div>
    </div>
  );
};

// Main Data Explorer component
const DataExplorer = ({
  data = [],
  title,
  description,
  filters = [],
  defaultView = 'table', // 'table', 'bar', 'line', 'pie'
  xKey = 'name',
  yKeys = ['value'],
  columns,
  allowViewSwitch = true,
  showExport = true,
}) => {
  const [currentView, setCurrentView] = useState(defaultView);
  const [filterValues, setFilterValues] = useState({});

  // Generate filter options from data
  const filterOptions = useMemo(() => {
    const options = {};
    filters.forEach((filter) => {
      const uniqueValues = [...new Set(data.map((row) => row[filter.key]).filter(Boolean))];
      options[filter.key] = uniqueValues.map((v) => ({ label: String(v), value: v }));
    });
    return options;
  }, [data, filters]);

  // Apply filters to data
  const filteredData = useMemo(() => {
    return data.filter((row) => {
      return filters.every((filter) => {
        const filterValue = filterValues[filter.key];
        if (!filterValue || (Array.isArray(filterValue) && filterValue.length === 0)) {
          return true;
        }
        const rowValue = row[filter.key];
        if (filter.type === 'range') {
          return rowValue >= filterValue[0] && rowValue <= filterValue[1];
        }
        if (filter.multiple) {
          return filterValue.includes(rowValue);
        }
        return rowValue === filterValue;
      });
    });
  }, [data, filters, filterValues]);

  const updateFilter = (key, value) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
  };

  const viewOptions = [
    { value: 'table', label: '📊 Table' },
    { value: 'bar', label: '📶 Bar' },
    { value: 'line', label: '📈 Line' },
    { value: 'pie', label: '🥧 Pie' },
  ];

  return (
    <div className="my-4 bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100">
        <div className="flex justify-between items-start flex-wrap gap-3">
          <div>
            {title && <h4 className="text-base font-semibold text-gray-900">{title}</h4>}
            {description && <p className="text-sm text-gray-500 mt-0.5">{description}</p>}
          </div>

          {allowViewSwitch && (
            <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
              {viewOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setCurrentView(option.value)}
                  className={`px-3 py-1 text-sm rounded-md transition-colors ${
                    currentView === option.value
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Filters */}
        {filters.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {filters.map((filter) => {
              if (filter.type === 'range') {
                const values = data.map((r) => r[filter.key]).filter((v) => typeof v === 'number');
                const min = Math.min(...values);
                const max = Math.max(...values);
                return (
                  <div key={filter.key} className="min-w-[200px]">
                    <RangeSlider
                      label={filter.label}
                      min={min}
                      max={max}
                      value={filterValues[filter.key] || [min, max]}
                      onChange={(val) => updateFilter(filter.key, val)}
                    />
                  </div>
                );
              }
              return (
                <FilterDropdown
                  key={filter.key}
                  label={filter.label}
                  options={filterOptions[filter.key] || []}
                  value={filterValues[filter.key]}
                  onChange={(val) => updateFilter(filter.key, val)}
                  multiple={filter.multiple}
                />
              );
            })}
            {Object.keys(filterValues).some((k) => {
              const v = filterValues[k];
              return v && (Array.isArray(v) ? v.length > 0 : true);
            }) && (
              <button
                onClick={() => setFilterValues({})}
                className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                Clear filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-0">
        {currentView === 'table' ? (
          <InteractiveTable
            data={filteredData}
            columns={columns}
            searchable
            sortable
            highlightMax
            highlightMin
            showExport={showExport}
          />
        ) : (
          <div className="p-4">
            <InteractiveChart
              type={currentView}
              data={filteredData}
              xKey={xKey}
              yKeys={yKeys}
              height={350}
              showExport={showExport}
            />
          </div>
        )}
      </div>

      {/* Footer with data summary */}
      <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 text-sm text-gray-500">
        Showing {filteredData.length} of {data.length} records
        {Object.keys(filterValues).length > 0 && ' (filtered)'}
      </div>
    </div>
  );
};

export { FilterDropdown, RangeSlider, DataExplorer };
export default DataExplorer;
